import { useEffect, useMemo, useState } from "react";
import { useT, type I18nUsageEntry, type I18nSource } from "@/lib/i18n";

const STORAGE_KEY = "lc_i18n_debug";

function readEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (localStorage.getItem(STORAGE_KEY) === "1") return true;
    const p = new URLSearchParams(window.location.search);
    if (p.get("debugI18n") === "1" || p.get("debug") === "i18n") return true;
  } catch {}
  return false;
}

const SOURCE_BADGE: Record<I18nSource, { label: string; cls: string }> = {
  direct: { label: "OK", cls: "bg-emerald-600 text-white" },
  "fallback-cs": { label: "CS", cls: "bg-amber-500 text-white" },
  missing: { label: "MISS", cls: "bg-red-600 text-white" },
};

export function I18nDebugPanel() {
  const { lang } = useT();
  const [enabled, setEnabled] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(true);
  const [entries, setEntries] = useState<I18nUsageEntry[]>([]);
  const [filter, setFilter] = useState<"" | I18nSource>("");
  const [q, setQ] = useState("");

  useEffect(() => {
    setEnabled(readEnabled());
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const w = window as unknown as { __i18nUsage?: I18nUsageEntry[] };
    setEntries([...(w.__i18nUsage ?? [])]);
    let raf = 0;
    const onUsage = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        setEntries([...(w.__i18nUsage ?? [])]);
      });
    };
    window.addEventListener("i18n:usage", onUsage as EventListener);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("i18n:usage", onUsage as EventListener);
    };
  }, [enabled]);

  const dedupedForPage = useMemo(() => {
    // Latest resolved value per key
    const map = new Map<string, I18nUsageEntry>();
    for (const e of entries) map.set(e.key, e);
    return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
  }, [entries]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return dedupedForPage.filter((e) => {
      if (filter && e.source !== filter) return false;
      if (!needle) return true;
      return e.key.toLowerCase().includes(needle) || e.value.toLowerCase().includes(needle);
    });
  }, [dedupedForPage, filter, q]);

  const counts = useMemo(() => {
    const c = { direct: 0, "fallback-cs": 0, missing: 0 } as Record<I18nSource, number>;
    for (const e of dedupedForPage) c[e.source]++;
    return c;
  }, [dedupedForPage]);

  if (!enabled) return null;

  const toggleEnabled = (v: boolean) => {
    setEnabled(v);
    try {
      if (v) localStorage.setItem(STORAGE_KEY, "1");
      else localStorage.removeItem(STORAGE_KEY);
    } catch {}
  };

  return (
    <div className="fixed bottom-3 left-3 z-[9999] w-[360px] max-w-[calc(100vw-1rem)] rounded-lg border border-black/10 bg-white text-foreground shadow-2xl">
      <div className="flex items-center justify-between border-b border-black/10 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full bg-sky-500" />
          <strong className="text-sm">i18n debug</strong>
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase">{lang}</span>
        </div>
        <div className="flex gap-1">
          <button className="rounded px-2 py-0.5 text-xs hover:bg-muted" onClick={() => setOpen((v) => !v)}>
            {open ? "−" : "+"}
          </button>
          <button
            className="rounded px-2 py-0.5 text-xs hover:bg-muted"
            onClick={() => toggleEnabled(false)}
            title="Vypnout debug panel"
          >
            ×
          </button>
        </div>
      </div>

      {open && (
        <div className="space-y-2 p-3 text-sm">
          <div className="flex gap-1 text-[11px]">
            {(["", "direct", "fallback-cs", "missing"] as const).map((s) => {
              const label =
                s === "" ? `Vše (${dedupedForPage.length})`
                : s === "direct" ? `OK (${counts.direct})`
                : s === "fallback-cs" ? `CS fallback (${counts["fallback-cs"]})`
                : `Chybí (${counts.missing})`;
              const active = filter === s;
              return (
                <button
                  key={s || "all"}
                  onClick={() => setFilter(s)}
                  className={`rounded border px-1.5 py-0.5 ${active ? "border-sky-600 bg-sky-600 text-white" : "border-black/15 bg-white hover:bg-muted"}`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Hledat klíč nebo text…"
            className="w-full rounded border border-black/15 px-2 py-1 text-xs"
          />

          <div className="max-h-[50vh] overflow-auto rounded border border-black/10">
            {filtered.length === 0 ? (
              <p className="p-2 text-xs text-muted-foreground">
                Zatím žádné volání překladů. Klikej po stránce a texty se objeví zde.
              </p>
            ) : (
              <ul className="divide-y divide-black/5">
                {filtered.map((e) => {
                  const badge = SOURCE_BADGE[e.source];
                  return (
                    <li key={e.key} className="px-2 py-1 text-[11px]">
                      <div className="flex items-center gap-1">
                        <span className={`rounded px-1 text-[9px] font-bold uppercase ${badge.cls}`}>
                          {badge.label}
                        </span>
                        <span className="rounded bg-muted px-1 font-mono text-[9px] uppercase">{e.lang}</span>
                        <span className="rounded bg-muted/60 px-1 font-mono text-[9px] uppercase">{e.via}</span>
                        <code className="ml-1 truncate font-mono text-[10px]" title={e.key}>{e.key}</code>
                      </div>
                      <div className="truncate pl-1 text-foreground/80" title={e.value}>{e.value}</div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <p className="text-[10px] text-muted-foreground">
            Zdroje: <b>OK</b> = z aktivního jazyka; <b>CS fallback</b> = klíč chybí, použita čeština;
            <b> Chybí</b> = klíč nikde. Sloupec <b>via</b>: <code>context</code> (t()) nebo <code>static</code> (translateStatic).
            Domain slovníky (reviews / suppliers / menu-categories) mají vlastní testy.
          </p>
        </div>
      )}
    </div>
  );
}
